import dayjs from 'dayjs';
import { useRef, useState, useEffect, useCallback } from 'react';

import axios, { endpoints, getAccessToken, refreshAccessToken } from 'src/utils/axios';

import useActiveReportContext from 'src/stores/active-report-context';

import { toast } from 'src/components/snackbar';

import { fileToAttachmentPayload } from '../utils/attachments';
import { linkifyEmails } from '../components/linkified-message';
import { buildHistory, HISTORY_LOADING_ID } from '../utils/history';
import {
  tooLargeAnswer,
  getRequestBytes,
  MAX_REQUEST_BYTES,
  RequestTooLargeError,
} from '../utils/too-large';

// ----------------------------------------------------------------------

const MAX_RETRIES = 2;
const RETRY_DELAY_BASE_MS = 1500;

// ----------------------------------------------------------------------

export function useKnowledgeChat({
  activeConversation,
  activeConversationId,
  appendMessages,
  updateMessageById,
  updateSubject,
  updateUsage,
  upsertConversationFromServer,
  currentUserId = 'me',
  assistantSenderId = 'support',
  modelArn = '',
  // When true, this chat never attaches the open report's context — used by the widget's
  // "Global Insights" mode so it can be forced to the global assistant even while a report
  // page (which keeps report context populated for as long as it's mounted) is open behind it.
  forceGlobalChat = false,
}) {
  const [sending, setSending] = useState(false);
  const {
    resource: activeReportResource,
    captureStatus: reportCaptureStatus,
    snapshotResolved,
    snapshotUploadErrorShown,
    setSnapshotResolved,
    markSnapshotUploadErrorShown,
  } = useActiveReportContext();
  const activeIdRef = useRef(activeConversationId);
  const abortControllerRef = /** @type {React.MutableRefObject<AbortController | null>} */ (useRef(null));

  useEffect(() => {
    activeIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const prevConversationIdRef = useRef(activeConversationId);
  useEffect(() => {
    if (prevConversationIdRef.current !== activeConversationId) {
      prevConversationIdRef.current = activeConversationId;
      setSnapshotResolved(false);
    }
  }, [activeConversationId, setSnapshotResolved]);

  const cancelMessage = useCallback(() => {
    abortControllerRef.current?.abort();
    if (activeConversationId) {
      axios.post(endpoints.knowledge.cancel, { conversation_id: activeConversationId }).catch(() => {});
    }
  }, [activeConversationId]);

  const sendMessage = useCallback(
    async (text, files = []) => {
      const trimmedText = (text || '').trim();
      if (!trimmedText && !files.length) return;

      // The AI backend still requires non-blank query text; fall back to a
      // generic prompt when the user only attached files.
      const effectiveText = trimmedText || 'Please analyze the attached file(s).';

      const messages = activeConversation?.messages || [];
      const history = buildHistory(messages);
      const isFirstMessage = messages.length === 0;
      const convIdWhenSending = activeConversationId;

      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      const attachmentsMeta = files.map((file) => ({
        name: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      }));

      const userMsg = {
        id: `user-${Date.now()}`,
        body: trimmedText,
        contentType: 'text',
        senderId: currentUserId,
        createdAt: new Date().toISOString(),
        attachments: attachmentsMeta,
      };

      const loadingMsg = {
        id: HISTORY_LOADING_ID,
        body: 'Searching your knowledge...',
        contentType: 'text',
        senderId: assistantSenderId,
        createdAt: new Date().toISOString(),
      };

      appendMessages([userMsg, loadingMsg]);
      setSending(true);

      if (isFirstMessage && updateSubject) {
        axios
          .post(
            endpoints.knowledge.title,
            {
              query: effectiveText,
              conversationId: convIdWhenSending,
              subject: activeConversation?.subject,
              model_arn: modelArn,
            },
            { signal }
          )
          .then(({ data }) => {
            if (activeIdRef.current === convIdWhenSending && data?.title) {
              updateSubject(data.title);
              if (data.usage && typeof updateUsage === 'function') {
                updateUsage(data.usage);
              }
            }
          })
          .catch(() => {});
      }

      const attachments = files.length ? await Promise.all(files.map(fileToAttachmentPayload)) : [];
      let includesReportContext = false;

      try {
        // The open report is exported lazily, on the first message sent about this run.
        let reportContext = null;
        if (activeReportResource && !forceGlobalChat) {
          await useActiveReportContext.getState().ensureCaptured();
          const { resource, params, data: reportData, pdf: reportPdf } = useActiveReportContext.getState();
          if (resource && reportData) {
            includesReportContext = true;
            reportContext = { resource, params, data: reportData, pdf: reportPdf || '' };
          } else if (resource) {
            // The export failed: still send which report is open, so the question stays in
            // Report Insights (and the assistant can say the data couldn't be loaded) rather than
            // silently going to Global Insights.
            includesReportContext = true;
            reportContext = { resource, params, data: '', pdf: '' };
          }
        }

        // Ensure we have a valid access token before hitting the chat endpoint.
        if (!getAccessToken()) {
          await refreshAccessToken();
        }

        const payload = {
          query: effectiveText,
          history,
          conversation_id: convIdWhenSending,
          subject: activeConversation?.subject,
          model_arn: modelArn,
          user_date: dayjs().format('YYYY-MM-DD (dddd)'),
          ...(attachments?.length && { attachments }),
          ...(reportContext && { report_context: reportContext }),
        };
        const tooLargeMessage = tooLargeAnswer({
          hasAttachments: attachments.length > 0,
          hasReport: !!reportContext,
        });
        if (getRequestBytes(payload) > MAX_REQUEST_BYTES) {
          throw new RequestTooLargeError(tooLargeMessage);
        }

        let data;
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          if (attempt > 0) {
            const delay = RETRY_DELAY_BASE_MS * attempt;
            updateMessageById(HISTORY_LOADING_ID, (prev) => ({
              ...prev,
              body: `Connection issue, retrying... (${attempt}/${MAX_RETRIES})`,
            }));
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
          try {
            const { data: responseData } = await axios.post(
              endpoints.knowledge.conversationQuery,
              payload,
              { _skipNetworkErrorToast: true, signal }
            );
            data = responseData;
            break;
          } catch (retryErr) {
            if (retryErr?.response?.status === 413) {
              throw new RequestTooLargeError(tooLargeMessage);
            }
            if (retryErr?.code !== 'ERR_NETWORK' || attempt >= MAX_RETRIES) {
              throw retryErr;
            }
          }
        }

        const {
          answer: answerBody,
          usage,
          conversation: serverConversation,
          reportContextStatus,
          reportContextError,
          attachmentsStatus,
          attachmentsError,
        } = data;

        updateMessageById(HISTORY_LOADING_ID, () => ({
          id: `support-${Date.now()}`,
          body: answerBody,
          contentType: 'text',
          senderId: assistantSenderId,
          createdAt: new Date().toISOString(),
        }));

        if (usage && typeof updateUsage === 'function') {
          updateUsage(usage);
        }

        if (serverConversation && typeof upsertConversationFromServer === 'function') {
          upsertConversationFromServer(serverConversation);
        }

        if (includesReportContext) {
          setSnapshotResolved(true);
          if (reportContextStatus === 'failed' && !snapshotUploadErrorShown) {
            markSnapshotUploadErrorShown();
            toast.error(linkifyEmails(reportContextError || 'The report context could not be uploaded.'), {
              id: 'report-context-upload-failed',
            });
          }
        }

        if (attachmentsStatus === 'failed') {
          toast.error(linkifyEmails(attachmentsError || 'The attached file(s) could not be uploaded.'), {
            id: 'chat-attachments-upload-failed',
          });
        }
      } catch (err) {
        if (includesReportContext) {
          setSnapshotResolved(true);
        }
        if (err?.code === 'ERR_CANCELED' || err?.name === 'AbortError' || err?.name === 'CanceledError') {
          updateMessageById(HISTORY_LOADING_ID, (prev) => ({
            ...prev,
            id: `support-${Date.now()}`,
            body: 'Response stopped.',
            senderId: assistantSenderId,
          }));
        } else if (err instanceof RequestTooLargeError) {
          updateMessageById(HISTORY_LOADING_ID, (prev) => ({
            ...prev,
            id: `support-${Date.now()}`,
            body: err.message,
            senderId: assistantSenderId,
          }));
        } else {
          const message = err?.response?.data?.detail || err?.message || 'Failed to query knowledge';
          const isCostLimitError = err?.response?.status === 403 && message.includes('cost limit reached');
          if (!isCostLimitError) {
            toast.error(message);
          }
          updateMessageById(HISTORY_LOADING_ID, (prev) => ({
            ...prev,
            id: `support-${Date.now()}`,
            body: `Sorry, something went wrong: ${message}`,
            senderId: assistantSenderId,
          }));
        }
      } finally {
        setSending(false);
        abortControllerRef.current = null;
      }
    },
    [
      activeConversation,
      activeConversationId,
      activeReportResource,
      appendMessages,
      modelArn,
      forceGlobalChat,
      upsertConversationFromServer,
      updateMessageById,
      updateSubject,
      updateUsage,
      setSnapshotResolved,
      snapshotUploadErrorShown,
      markSnapshotUploadErrorShown,
    ]
  );

  const usesReportContext = !!activeReportResource && !forceGlobalChat;
  const exportingReportContext = usesReportContext && reportCaptureStatus === 'exporting';
  const savingReportContext = sending && usesReportContext && !snapshotResolved;

  return {
    sendMessage,
    cancelMessage,
    sending,
    exportingReportContext,
    savingReportContext,
    loadingMessageId: sending ? HISTORY_LOADING_ID : null,
  };
}
